import { useEffect, useRef } from 'react';
import { Platform } from 'react-native';
import { usePathname } from 'expo-router';

import { useSupabaseAuth } from '../contexts/SupabaseAuthContext';
import { detectMobileWebOs } from '../../onboarding/helpers/detectMobileWebOs.helper';
import {
  WEB_NATIVE_APP_PROMO_COPY,
  WEB_NATIVE_APP_PROMO_SHOW_DELAY_MS,
} from '../constants/webNativeAppPromo.constants';
import {
  readWebNativeAppPromoStorage,
  recordWebNativeAppPromoShown,
  shouldShowWebNativeAppPromo,
} from '../helpers/webNativeAppPromo.helper';
import { useDialog } from './useDialog';

/**
 * Mobile-web install promo for signed-in /app/* users — at most once per 24h
 * (cooldown from last shown/dismissed), until permanently opted out.
 * Uses useEffect only — never refires on window/tab focus.
 */
export function useWebNativeAppPromo(): void {
  const { isLoaded, isSignedIn } = useSupabaseAuth();
  const pathname = usePathname();
  const { showDialog } = useDialog();
  const hasShownRef = useRef(false);

  useEffect(() => {
    if (Platform.OS !== 'web') return;
    if (hasShownRef.current) return;
    if (!isLoaded || !isSignedIn) return;

    const mobileOs = detectMobileWebOs();
    const storage = readWebNativeAppPromoStorage();
    const eligible = shouldShowWebNativeAppPromo({
      platform: Platform.OS,
      mobileOs,
      isLoaded,
      isSignedIn,
      pathname,
      storage,
      now: new Date(),
    });

    if (!eligible) return;

    const timer = setTimeout(() => {
      // Timestamps are set only when the dialog actually fires — if the user
      // navigates during the delay, cleanup clears the timer and the next
      // eligible effect run re-arms instead of consuming the 24h cooldown.
      hasShownRef.current = true;
      recordWebNativeAppPromoShown();
      void showDialog({
        title: WEB_NATIVE_APP_PROMO_COPY.TITLE,
        message: WEB_NATIVE_APP_PROMO_COPY.MESSAGE,
        type: 'default',
        confirmationMode: 'nativeAppPromo',
        confirmText: WEB_NATIVE_APP_PROMO_COPY.CONFIRM,
        cancelText: WEB_NATIVE_APP_PROMO_COPY.CANCEL,
        dismissible: true,
      });
    }, WEB_NATIVE_APP_PROMO_SHOW_DELAY_MS);

    return () => {
      clearTimeout(timer);
    };
  }, [isLoaded, isSignedIn, pathname, showDialog]);
}
